import { ActionContext, GetterTree } from 'vuex';
import { cloneDeep } from 'lodash';
import { getCategories } from '@/services/categoryService';
import {
  KnowledgeCategory, Knowledge, KnowledgeContent, Store, KnowledgeType, EmergencyContent, ContentType
} from '@/models';
import router from '@/router';
import { searchContentsByKeyword, getEmergencyContents, searchContentByContentId } from '@/services/elasticSearchService';
import i18n from '@/i18n';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const { t } = i18n.global;

const getSelectedCategory = (selected: string, categories: KnowledgeCategory[]): KnowledgeCategory => {
  return categories.find((category: KnowledgeCategory) => category.name === selected) as KnowledgeCategory;
};

const isKosapoDeviceSelected = (categorySelections: string[]): boolean => {
  return categorySelections.length === 2 &&
    categorySelections[0] === KnowledgeType.damage &&
    categorySelections[1] !== KnowledgeType.nonCoverageDevice;
};

const shouldPerformElasticSearch = (categorySelections: string[]): boolean => {
  return (categorySelections.length > 2);
};

const state: Knowledge = {
  isSearchByKeyboard: false,
  isContentSearching: false,
  faqBack: false,
  categorySelections: [],
  categories: [],
  contents: [],
  selectedContent: {
    contentID: '',
    title: '',
    description: '',
    image: '',
    contentURL: ''
  },
  emergencyContents: [],
  selectedContentType: ''
};

const getters = {
  selectedKnowledgeCategory (moduleState: Knowledge): string {
    return moduleState.categorySelections[0];
  },
  isKnowledgeTopLayer (moduleState: Knowledge): boolean {
    return moduleState.categorySelections.length === 0;
  },
  knowledgeCategorySelections (moduleState: Knowledge): string[] {
    return moduleState.categorySelections;
  },
  currentKnowledgeCategories (moduleState: Knowledge): string[] {
    let { categories } = moduleState;
    if (moduleState.categorySelections.length !== 0) {
      moduleState.categorySelections.forEach((categorySelection) => {
        const selectedCategory = getSelectedCategory(categorySelection, categories);

        if (selectedCategory?.children) {
          categories = selectedCategory.children;
        } else if (!isKosapoDeviceSelected(moduleState.categorySelections)) {
          // only set the category tree to zero for non kosapo claim
          categories = [];
        }
      });
    }

    return categories.map((category: KnowledgeCategory) => {
      return category.name;
    });
  },
  knowledgeContents (moduleState: Knowledge): KnowledgeContent[] {
    return moduleState.contents;
  },
  selectedKnowledgeContent (moduleState: Knowledge): KnowledgeContent {
    return moduleState.selectedContent;
  },
  shouldSearchTroubleshootingContents (moduleState: Knowledge, moduleGetters: GetterTree<Knowledge, Store>): boolean {
    return moduleGetters.currentKnowledgeCategories.length === 0;
  },
  selectedSymptom (moduleState: Knowledge, moduleGetters: GetterTree<Knowledge, Store>): string {
    if (moduleGetters.currentKnowledgeCategories.length === 0) {
      return moduleState.categorySelections[moduleState.categorySelections.length - 1];
    }

    return '';
  },
  emergencyContents (moduleState: Knowledge): KnowledgeContent[] {
    return moduleState.emergencyContents;
  }
};

const mutations = {
  setIsContentsSearchByKeyboard (moduleState: Knowledge, isSearchByKeyboard: boolean): void {
    moduleState.isSearchByKeyboard = isSearchByKeyboard;
  },
  setCategories (moduleState: Knowledge, categories: KnowledgeCategory[]): void {
    moduleState.categories = categories;
  },
  setCategorySelections (moduleState: Knowledge, categorySelections: string[]): void {
    moduleState.categorySelections = categorySelections;
  },
  setContents (moduleState: Knowledge, contents: KnowledgeContent[]): void {
    moduleState.contents = contents;
  },
  setSelectedContent (moduleState: Knowledge, selectedContent: KnowledgeContent): void {
    moduleState.selectedContent = selectedContent;
  },
  setEmergencyContents (moduleState: Knowledge, emergencyContents: EmergencyContent[]): void {
    moduleState.emergencyContents = emergencyContents;
  }
};

const actions = {
  async getKnowledgeCategoriesByPath (action: ActionContext<Knowledge, Store>, path: string) {
    const spath = path.split('-');
    spath.shift();
    const selections: string[] = [];
    const categoriesCopy = action.state.categories.length ? [...action.state.categories] : [...await getCategories()];
    spath.forEach(item => {
      const index = parseInt(item);
      selections.push(categoriesCopy[index].name);
      const newArray = categoriesCopy[index].children;

      // replace array
      if (newArray) {
        categoriesCopy.splice(0, categoriesCopy.length, ...newArray);
      }
    });
    action.commit('setCategorySelections', selections);
    if (shouldPerformElasticSearch(selections) ||
    action.getters.shouldSearchTroubleshootingContents) {
      action.dispatch('performElasticSearch', selections);
    }
  },

  async updatePathBySelection (action: ActionContext<Knowledge, Store>, select: {categorySelection: string; currentPath: string}) {
    const spath = select.currentPath.split('-');
    const categoriesCopy = [...action.state.categories];
    let index = 0;
    let newArray;
    spath.forEach((selected, count) => {
      try {
        if (spath.length > 1) {
          newArray = categoriesCopy[parseInt(spath[count + 1])].children;
        } else {
          newArray = [...categoriesCopy];
        }

        // replace array
        if (newArray) {
          categoriesCopy.splice(0, categoriesCopy.length, ...newArray);
          index = newArray.findIndex(cat => cat.name === select.categorySelection);
        }
      } catch (e) {
        console.log('end');
      }
    });
    if (index >= 0) {
      const newPath = select.currentPath + '-' + index;
      router.push(`/ivr/troubleshooting/${newPath}`);
    }
  },

  addKnowledgeCategorySelection (action: ActionContext<Knowledge, Store>, categorySelection: string) {
    const categorySelections = action.state.categorySelections.slice(0);
    categorySelections.push(categorySelection);
    action.commit('setCategorySelections', categorySelections);

    if (shouldPerformElasticSearch(categorySelections) ||
    action.getters.shouldSearchTroubleshootingContents) {
      action.dispatch('performElasticSearch', categorySelections);
    }
  },

  setKnowledgeCategorySelections (action: ActionContext<Knowledge, Store>, categorySelections: string[]) {
    action.commit('setCategorySelections', categorySelections);
  },

  removeKnowledgeCategorySelection (action: ActionContext<Knowledge, Store>) {
    const categorySelections = action.state.categorySelections.slice(0);
    categorySelections.pop();
    action.commit('setCategorySelections', categorySelections);

    // if knowledge layer equal 3, and it is データ移行, clear knowledge contents
    // else clear contents when it is less than 3
    if (categorySelections.length < 3) {
      action.dispatch('clearKnowledgeContents');
    }
  },

  clearKnowledgeCategorySelections (action: ActionContext<Knowledge, Store>) {
    action.commit('setIsContentsSearchByKeyboard', false);
    action.commit('setCategorySelections', []);
  },

  async getKnowledgeCategories (action: ActionContext<Knowledge, Store>) {
    const categories = await getCategories();
    // cloning categories to avoid contaminating the original list
    const categoriesClone = cloneDeep(categories);
    const filteredCategories: KnowledgeCategory[] = categoriesClone;
    action.commit('setCategories', filteredCategories);
  },

  performElasticSearch (action: ActionContext<Knowledge, Store>, categorySelections: string[]) {
    const isDataMigration = categorySelections[1] === KnowledgeType.dataMigration;
    const { contractDevice, currentDevice } = action.rootGetters;

    // Remove checkDefectSolutions from elastic search array since we added device selection page after "症状の改善方法を調べる".
    const mappedCategorySelections = categorySelections.filter(selection => selection !== KnowledgeType.checkDefectSolutions).map((selection, index) => {
      if (isDataMigration) {
        let dataMigrationSelection = selection;

        if (selection === KnowledgeType.contractDevice) {
          dataMigrationSelection = contractDevice.dataMigrationCategory;
        } else if (selection === KnowledgeType.currentDevice) {
          dataMigrationSelection = currentDevice.dataMigrationCategory;
        }

        // add から for selection index number 2 データ移行
        return index === 2
          ? t('from device', {
            device: dataMigrationSelection
          })
          : dataMigrationSelection;
      }

      if (selection === KnowledgeType.contractDevice) {
        return contractDevice.series;
      }

      if (selection === KnowledgeType.currentDevice ||
        selection === KnowledgeType.diagnoseDefect) {
        return currentDevice.series;
      }

      return selection;
    });

    action.dispatch('searchKnowledgeContents', ({
      searchString: mappedCategorySelections.join(' '),
      categories: mappedCategorySelections,
      isSearchByKeyboard: false
    }));
  },

  async searchKnowledgeContents (action: ActionContext<Knowledge, Store>, params: { searchString: string; categories?: string[]; isSearchByKeyboard: boolean }) {
    if (!params.categories) params.categories = [];
    // params.categories.unshift(...action.getters.subscriberCategories);

    const contents = await searchContentsByKeyword(params.searchString, params.categories);
    action.commit('setContents', contents);
    action.commit('setIsContentsSearchByKeyboard', params.isSearchByKeyboard);
  },

  updateKnowledgeContents (action: ActionContext<Knowledge, Store>, contents: KnowledgeContent[]) {
    action.commit('setContents', contents);
  },

  async updateEmergencyContents (action: ActionContext<Knowledge, Store>) {
    const emergencyContents = await getEmergencyContents();
    action.commit('setEmergencyContents', emergencyContents);
  },

  clearKnowledgeContents ({ commit }: ActionContext<Knowledge, Store>) {
    commit('setContents', []);
    commit('setIsContentsSearchByKeyboard', false);
  },

  async getIsTimelineContent (action: ActionContext<Knowledge, Store>, contentID: string) {
    const content = await searchContentByContentId(contentID);
    return content.type === ContentType.timeline;
  },

  async selectKnowledgeContent (action: ActionContext<Knowledge, Store>, contentID: string) {
    const selectedContent = action.state.contents.find(content => content.contentID === contentID);

    // knowledge contentType will be either troubleshooting or faq
    const contentType = 'troubleshooting';

    action.commit('setSelectedContent', selectedContent);
    router.push(`/ivr/content/${contentID}?type=${contentType}`);
  },

  async selectEmergencyContent (action: ActionContext<Knowledge, Store>, contentID: string) {
    const selectedContent = action.state.emergencyContents.find(content => content.contentID === contentID);

    // knowledge contentType will be either troubleshooting or faq
    const contentType = 'emergency';

    action.commit('setSelectedContent', selectedContent);

    router.push(`/ivr/content/${contentID}?type=${contentType}`);
  },

  clearKnowledgeContentSelection ({ commit }: ActionContext<Knowledge, Store>) {
    commit('setSelectedContent', {
      title: '',
      body: ''
    });
  }
};

export const knowledge = {
  state,
  getters,
  mutations,
  actions
};
